import currency from 'currency.js';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import logo from '../../../assets/images/logo_new.png';
import { showCustomToast } from '../../../controls/CustomToast';
import useAuthDataStore from '../../../hooks/store/useAuthDataStore';
import { getRetailerBillingAddressSvc } from '../../../services/retailer';
import { getRechargeHistoryByIdSvc } from '../../../services/wallet';
import { isTaxInvoiceTopUp } from './constant';
import './rechargeInvoicePage.scss';

// Constants
const REFUND_TYPES = [8, 9];
const STATUS_SUCCEED = 'succeed';
const WALLET_HISTORY_PATH = '/wallet/history';

// Helper functions
const formatCurrency = (amount) => {
  return currency(amount, {
    symbol: '$',
    decimal: '.',
    separator: ','
  }).format();
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  return moment.tz(dateString, 'Australia/Sydney').format('DD/MM/YYYY');
};

const getCountryName = (country) => {
  const countryMap = {
    AU: 'Australia',
    NZ: 'New Zealand'
  };
  return countryMap[country] || country || '';
};

const getBillingName = (billingInfo) => {
  if (billingInfo?.firstname && billingInfo?.lastname) {
    return `${billingInfo.firstname} ${billingInfo.lastname}`;
  }
  return billingInfo?.company || 'N/A';
};

const getItemName = (record, hasProcessingFee, formatCurrencyFn) => {
  const isRefund = REFUND_TYPES.includes(record.credit_process_type);
  let name = isRefund ? 'REFUND' : 'RECHARGE';

  if (hasProcessingFee) {
    const rechargeAmount = Math.abs(record.amount || 0);
    name = `${name} (${formatCurrencyFn(rechargeAmount)}) PROCESSING FEE`;
  }

  return name;
};

const getAmount = (record, hasProcessingFee) => {
  return hasProcessingFee
    ? Math.abs(record.processing_fee || 0)
    : Math.abs(record.amount || 0);
};

const RechargeInvoicePage = () => {
  const [billingInfo, setBillingInfo] = useState(null);
  const [rechargeRecord, setRechargeRecord] = useState(null);
  const { getRetailerId } = useAuthDataStore.getState();
  const retailerId = getRetailerId();
  const { rechargeId } = useParams();
  const history = useHistory();

  useEffect(() => {
    if (!rechargeId || !retailerId) {
      history.push(WALLET_HISTORY_PATH);
      return;
    }

    const fetchData = async () => {
      try {
        const res = await getRechargeHistoryByIdSvc(parseInt(rechargeId));
        const record = res?.data || res;

        if (!record) {
          throw new Error('Recharge record not found.');
        }

        if (record.status !== STATUS_SUCCEED) {
          throw new Error(
            'Only successful recharge records can generate invoices.'
          );
        }

        setRechargeRecord(record);

        const billingRes = await getRetailerBillingAddressSvc(retailerId);
        if (!billingRes.success) {
          throw new Error('Error loading billing information.');
        }

        setBillingInfo(billingRes.data);
      } catch (err) {
        if (err && err.code === "ERR_CANCELED") {
          console.warn('error canceled skip...', err);
          return;
        }
        showCustomToast(`Error loading data. ${err.message}`);
        history.push(WALLET_HISTORY_PATH);
      }
    };

    fetchData();
  }, [rechargeId, retailerId, history]);

  useEffect(() => {
    if (rechargeRecord && billingInfo) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          window.print();
        }, 500);
      });
    }
  }, [rechargeRecord, billingInfo]);

  if (!rechargeRecord || !billingInfo) {
    return null;
  }

  const hasProcessingFee = isTaxInvoiceTopUp(rechargeRecord);

  const itemName = getItemName(
    rechargeRecord,
    hasProcessingFee,
    formatCurrency
  );
  const amount = getAmount(rechargeRecord, hasProcessingFee);
  const documentType = hasProcessingFee ? 'TAX INVOICE' : 'RECEIPT';

  return (
    <div className="recharge-invoice-page">
      <div className="receipt-header">
        <div className="header-logo">
          <img src={logo} className="logo" alt="DROPSHIPZONE" />
        </div>
      </div>

      <div className="receipt-content">
        <div className="col2-set">
          <div className="column-1">
            <div className="detail-header">BILLED TO</div>
            <address>
              {getBillingName(billingInfo)}
              <br />
              {billingInfo?.street && (
                <>
                  {billingInfo.street}
                  <br />
                </>
              )}
              {billingInfo?.suburb &&
                billingInfo?.state &&
                billingInfo?.postcode && (
                  <>
                    {billingInfo.suburb}, {(billingInfo.state ?? '')}{' '}
                    {billingInfo.postcode}
                    <br />
                  </>
                )}
              {getCountryName(billingInfo?.country)}
              <br />
              {billingInfo?.email || ''}
            </address>
          </div>
          <div className="column-2">
            <div className="detail-header">{documentType}</div>
            <div className="receipt-details">
              <div className="receipt-detail-item">
                Recharge Number:{' '}
                <span className="receipt-value">#{rechargeRecord.id}</span>
              </div>
              <div className="receipt-detail-item">
                Recharge Date:{' '}
                <span className="receipt-value">
                  {formatDate(rechargeRecord.effect_time)}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="items-section">
          <div className="items-header">
            <div className="items-header-cell">Items</div>
            <div className="items-header-cell">Subtotal</div>
            <div className="items-header-cell">Payment Method</div>
            <div className="items-header-cell">
              Total{hasProcessingFee ? ' (incl. GST)' : ''}
            </div>
          </div>
          <div className="items-row">
            <div className="items-row-cell">{itemName}</div>
            <div className="items-row-cell">{formatCurrency(amount)}</div>
            <div className="items-row-cell">
              {rechargeRecord.payment_method || 'N/A'}
            </div>
            <div className="items-row-cell">{formatCurrency(amount)}</div>
          </div>
        </div>

        <div className="receipt-footer">
          <div className="footer-content">
            <div className="footer-row">
              <span>ABN: 50 115 804 432</span>
              <span className="footer-separator">|</span>
              <span>16-18 CATO ST, HAWTHORN EAST, VIC 3123, AUSTRALIA</span>
              <span className="footer-separator">|</span>
              <span>
                <a
                  href="https://www.dropshipzone.com.au/privacy_statement"
                  className="footer-link">
                  PRIVACY POLICY
                </a>
              </span>
              <span className="footer-separator">|</span>
              <span>
                <a
                  href="https://www.dropshipzone.com.au/terms-conditions"
                  className="footer-link">
                  TERMS & CONDITIONS
                </a>
              </span>
              <span className="footer-separator">|</span>
              <span>
                <a href="https://dropshipzone.com.au" className="footer-link">
                  DROPSHIPZONE.COM.AU
                </a>
              </span>
            </div>
            <div className="footer-copyright">
              © {new Date().getFullYear()} DROPSHIPZONE. ALL RIGHTS RESERVED.
            </div>
          </div>
        </div>

        <div className="buttons-set">
          <button
            type="button"
            title="Close Window"
            className="button close-button"
            onClick={() => window.close()}>
            <span>Close Window</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default RechargeInvoicePage;
